import { unwrap } from "./unwrap";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestCfg {
  url: string;
  method: HttpMethod;
  queryParams?: Record<string, any>;
  body?: Record<string, any>;
  headers?: Record<string, string>;
  requestConfig?: { encryptedParams?: string[]; encryptedBody?: string[] };
  responseConfig?: { encryptedResponse?: string[] };
}

export type ServiceAdapter = (cfg: RequestCfg) => Promise<unknown>;

export interface UiActions {
  beginGlobalLoading: () => any;
  endGlobalLoading: () => any;
  setProgress: (p: { key: string; percent: number }) => any;
  clearProgress: (p: { key: string }) => any;

  cancelTopModal: () => any;

  openConfirm: (opts: {
    title: string;
    subtitle?: string;
    confirmText?: string;
    cancelText?: string;
  }) => Promise<boolean>;

  openAlertSuccess: (title: string, subtitle?: string) => any;
  openAlertError: (title: string, subtitle?: string) => any;
  openAlertInfo: (title: string, subtitle?: string) => any;
}

export interface ReduxEnv {
  store: { dispatch: (a: any) => void };
  actions: UiActions;
}

export interface FactoryOptions {
  baseUrl?: string;
  defaultHeaders?: Record<string, string>;
  defaultRequestConfig?: RequestCfg["requestConfig"];
  defaultResponseConfig?: RequestCfg["responseConfig"];
  redux: ReduxEnv;
}

export interface MakeOptions<TInput, TRaw, TOut> {
  path: string;
  method: HttpMethod;
  mapInput?: (input: TInput) => Omit<RequestCfg, "url" | "method">;
  mapResponse?: (raw: any) => TOut;
  ui?: {
    showLoader?: boolean; // default: true
    requireConfirm?: boolean; // default: true
    showErrorModal?: boolean; // default: true
    showSuccessModal?: boolean; // default: false
    confirmText?: { title?: string; subtitle?: string; accept?: string; cancel?: string };
    successText?: { title?: string; subtitle?: string };
    errorText?: { title?: string; subtitle?: string };
    // progreso opcional por key
    progressKey?: string; // si lo pasas, podrás setProgress/clearProgress externamente
  };
}

export function createApiFactory(adapter: ServiceAdapter, opts: FactoryOptions) {
  const base = (opts.baseUrl ?? "").replace(/\/$/, "");

  function make<TInput = void, TRaw = unknown, TOut = TRaw>(ep: MakeOptions<TInput, TRaw, TOut>) {
    const url = `${base}${ep.path.startsWith("/") ? ep.path : `/${ep.path}`}`;

    return async (input: TInput extends void ? never : TInput): Promise<TOut> => {
      const {
        showLoader = true,
        requireConfirm = true,
        showErrorModal = true,
        showSuccessModal = false,
        confirmText,
        successText,
        errorText,
        progressKey,
      } = ep.ui ?? {};

      try {
        // Confirm
        if (requireConfirm) {
          const accepted = await opts.redux.actions.openConfirm({
            title: confirmText?.title ?? "¿Confirmar acción?",
            subtitle: confirmText?.subtitle ?? "",
            confirmText: confirmText?.accept ?? "Aceptar",
            cancelText: confirmText?.cancel ?? "Cancelar",
          });
          if (!accepted) throw new Error("Acción cancelada por el usuario");
        }

        // Loader ON (contador)
        if (showLoader) opts.redux.store.dispatch(opts.redux.actions.beginGlobalLoading());

        // Opcional: inicializar progreso
        if (progressKey)
          opts.redux.store.dispatch(
            opts.redux.actions.setProgress({ key: progressKey, percent: 0 })
          );

        const mapped = ep.mapInput ? ep.mapInput(input as TInput) : {};
        const raw = await adapter({
          url,
          method: ep.method,
          queryParams: mapped.queryParams,
          body: mapped.body,
          headers: { ...(opts.defaultHeaders ?? {}), ...(mapped.headers ?? {}) },
          requestConfig: mapped.requestConfig ?? opts.defaultRequestConfig,
          responseConfig: mapped.responseConfig ?? opts.defaultResponseConfig,
        });

        // Progreso al 100% (si aplica)
        if (progressKey)
          opts.redux.store.dispatch(
            opts.redux.actions.setProgress({ key: progressKey, percent: 100 })
          );

        const data = ep.mapResponse ? ep.mapResponse(raw) : (unwrap as any)(raw);

        if (showSuccessModal) {
          opts.redux.actions.openAlertSuccess(
            successText?.title ?? "Operación exitosa",
            successText?.subtitle ?? ""
          );
        }

        return data as TOut;
      } catch (err: any) {
        if (showErrorModal) {
          opts.redux.actions.openAlertError(
            errorText?.title ?? "Error",
            errorText?.subtitle ?? err?.message ?? "Falló la solicitud"
          );
        }
        throw err;
      } finally {
        if (progressKey)
          opts.redux.store.dispatch(opts.redux.actions.clearProgress({ key: progressKey }));
        if (showLoader) opts.redux.store.dispatch(opts.redux.actions.endGlobalLoading());
      }
    };
  }

  return { make };
}
